import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useSocket } from './SocketContext';
import api from '../services/apiService';

const MessagingContext = createContext();

export function MessagingProvider({ children }) {
  const { user } = useAuth();
  const socket = useSocket();
  const [unreadCount, setUnreadCount] = useState(0);
  const [newMessage, setNewMessage] = useState(null);
  // Private messaging is closed while a super admin acts as a member
  const actingAs = !!user?.act_as;

  // Fetch unread count on mount when user is set
  useEffect(() => {
    if (!user) return;
    if (actingAs) {
      setUnreadCount(0);
      return;
    }
    api.get('/messages/unread-count')
      .then(res => setUnreadCount(res.data.count))
      .catch(() => {}); // silent fail
  }, [user, actingAs]);

  // Socket listeners
  useEffect(() => {
    if (!socket || actingAs) return;

    const handleNewMessage = (message) => {
      setNewMessage(message);
      setUnreadCount(prev => prev + 1);
    };

    const handleUnreadCount = ({ count }) => {
      setUnreadCount(count);
    };

    socket.on('message:new', handleNewMessage);
    socket.on('message:unread-count', handleUnreadCount);

    return () => {
      socket.off('message:new', handleNewMessage);
      socket.off('message:unread-count', handleUnreadCount);
    };
  }, [socket, actingAs]);

  const clearNewMessage = useCallback(() => setNewMessage(null), []);

  return (
    <MessagingContext.Provider value={{ unreadCount, setUnreadCount, newMessage, clearNewMessage }}>
      {children}
    </MessagingContext.Provider>
  );
}

export function useMessaging() {
  return useContext(MessagingContext);
}
